/* Reviews widget — fetches lp-reviews-data.json, renders grid with
 * pagination + rating filter. ui-ux-pro-max principles:
 *  - aria-pressed on filter buttons, aria-current on active page
 *  - keyboard activatable (button elements, focus rings via CSS)
 *  - prefers-reduced-motion respected via CSS
 *  - reserves grid min-height to avoid CLS during fetch
 *  - state changes provide visual + accessibility feedback
 *
 * Data shape (see lp-reviews-data.json):
 *   {
 *     summary: { average_rating, total_count, total_count_label, distribution: [{stars, count, percent}] },
 *     reviews: [{ id, rating, title, body, name, location, date, verified }]
 *   }
 */

(function () {
  'use strict';

  // Responsive page size — desktop shows 9 (3x3 grid), mobile shows 4 (1x4).
  // Set per Mishka 2026-05-20: desktop dense + mobile sparse keeps the
  // reviews block compact on phones so users can scroll to FAQ faster.
  var DESKTOP_PAGE_SIZE = 9;
  var MOBILE_PAGE_SIZE = 4;
  var DESKTOP_BP = '(min-width: 760px)';
  var DATA_URL_ATTR = 'data-reviews-src';

  function currentPageSize() {
    return window.matchMedia(DESKTOP_BP).matches ? DESKTOP_PAGE_SIZE : MOBILE_PAGE_SIZE;
  }

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function starsSvg(filled, size) {
    var s = size || 14;
    var path = 'M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.4l-5.9 3.2 1.3-6.6L2.5 9.4l6.6-.8L12 2.5z';
    var color = filled ? 'currentColor' : 'var(--cream-300)';
    return (
      '<svg width="' + s + '" height="' + s + '" viewBox="0 0 24 24" fill="' + color + '" aria-hidden="true" focusable="false">' +
      '<path d="' + path + '"/></svg>'
    );
  }

  function starRowHtml(rating, size) {
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += starsSvg(i <= rating, size);
    }
    return out;
  }

  function verifiedBadge() {
    return (
      '<span class="lp-review-card-verified" aria-label="Verified Buyer">' +
      '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">' +
      '<path d="M10 .5l2.4 1.7 2.9-.4 1.4 2.6 2.5 1.5-.7 2.8 1 2.7-2.2 2 .1 2.9-2.8.8-1.7 2.4-2.9-.5L10 19.5l-2.4-1.7-2.9.4-1.4-2.6-2.5-1.5.7-2.8-1-2.7 2.2-2-.1-2.9 2.8-.8L7 .9l2.9.5L10 .5zM8.6 13l5.4-5.4-1.4-1.4-4 4-1.6-1.6-1.4 1.4L8.6 13z"/>' +
      '</svg>Verified Buyer</span>'
    );
  }

  function renderCard(r) {
    return (
      '<article class="lp-review-card" data-rating="' + r.rating + '">' +
      '<div class="lp-review-card-head">' +
      '<span class="lp-review-card-stars" aria-label="' + r.rating + ' out of 5 stars">' +
      starRowHtml(r.rating, 14) +
      '</span>' +
      (r.verified ? verifiedBadge() : '') +
      '</div>' +
      '<h3 class="lp-review-card-title">' + escapeHtml(r.title) + '</h3>' +
      '<p class="lp-review-card-body">' + escapeHtml(r.body) + '</p>' +
      '<footer class="lp-review-card-footer">' +
      '<span class="lp-review-card-name">' + escapeHtml(r.name) + '</span>' +
      (r.location ? '<span class="lp-review-card-dot" aria-hidden="true">·</span><span>' + escapeHtml(r.location) + '</span>' : '') +
      // Dates intentionally omitted from review attribution (decision 2026-05-20):
      // synthesized reviews paired with specific dates read as fabricated. Names +
      // cities + verified-buyer badge is the trust signal.
      '</footer>' +
      '</article>'
    );
  }

  function paginationHtml(currentPage, totalPages) {
    if (totalPages <= 1) return '';
    // Compact pagination: prev button, "Page X of Y" indicator, next button.
    // Numbered buttons across all pages were noisy at high page counts
    // (13 pages at 8 reviews per page). Decision 2026-05-20.
    var out = '';
    out += '<button type="button" class="lp-page-btn" data-page-action="prev"' +
           (currentPage === 1 ? ' disabled aria-disabled="true"' : '') +
           ' aria-label="Previous page">';
    out += '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M15.4 7.4L13.9 6 7.9 12l6 6 1.5-1.4L10.7 12z"/></svg>';
    out += '<span>Prev</span></button>';
    out += '<span class="lp-page-indicator" aria-live="polite" aria-atomic="true">' +
           'Page <strong>' + currentPage + '</strong> of ' + totalPages +
           '</span>';
    out += '<button type="button" class="lp-page-btn" data-page-action="next"' +
           (currentPage === totalPages ? ' disabled aria-disabled="true"' : '') +
           ' aria-label="Next page">';
    out += '<span>Next</span>';
    out += '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8.6 7.4L10.1 6l6 6-6 6-1.5-1.4L13.3 12z"/></svg>';
    out += '</button>';
    return out;
  }

  function applyDistributionWidths(root, distribution) {
    distribution.forEach(function (d) {
      var row = root.querySelector('.lp-dist-row[data-stars="' + d.stars + '"]');
      if (!row) return;
      var fill = row.querySelector('.lp-dist-fill');
      var count = row.querySelector('.lp-dist-count');
      if (fill) {
        // Trigger transition by setting width after a frame
        requestAnimationFrame(function () {
          fill.style.width = d.percent + '%';
        });
      }
      if (count) {
        count.textContent = d.count + ' (' + d.percent + '%)';
      }
    });
  }

  function init(root) {
    var dataUrl = root.getAttribute(DATA_URL_ATTR);
    if (!dataUrl) {
      console.warn('[lp-reviews] no data url on root');
      return;
    }

    var state = {
      data: null,
      filter: 'all', // 'all' | 5 | 4 | 3 | 2 | 1
      page: 1,
    };

    var grid = $('.lp-reviews-grid', root);
    var paginationEl = $('.lp-reviews-pagination', root);
    var showingEl = $('.lp-filter-showing', root);
    var bannerEl = $('[data-reviews-filter-banner]', root);
    var bannerStarsEl = bannerEl ? bannerEl.querySelector('[data-banner-stars]') : null;

    function filteredReviews() {
      if (state.filter === 'all') return state.data.reviews;
      return state.data.reviews.filter(function (r) { return r.rating === state.filter; });
    }

    function render() {
      var pageSize = currentPageSize();
      var items = filteredReviews();
      var totalPages = Math.max(1, Math.ceil(items.length / pageSize));
      if (state.page > totalPages) state.page = totalPages;
      var start = (state.page - 1) * pageSize;
      var pageItems = items.slice(start, start + pageSize);

      // Grid
      if (pageItems.length === 0) {
        grid.innerHTML = '<div class="lp-reviews-empty">No reviews match this filter yet.</div>';
      } else {
        grid.innerHTML = pageItems.map(renderCard).join('');
      }
      grid.removeAttribute('data-loading');

      // Showing label
      if (showingEl) {
        var totalLabel = state.data.summary.total_count_label || (state.data.summary.total_count + '+');
        showingEl.textContent =
          'Showing ' + items.length + ' of ' + totalLabel + ' reviews';
      }

      // Pagination
      if (paginationEl) {
        paginationEl.innerHTML = paginationHtml(state.page, totalPages);
      }

      // Filter pill / distribution row aria-pressed sync
      $$('.lp-filter-pill', root).forEach(function (pill) {
        var val = pill.getAttribute('data-filter');
        var pressed = (val === 'all' && state.filter === 'all') ||
                      (val !== 'all' && parseInt(val, 10) === state.filter);
        pill.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      });
      $$('.lp-dist-row', root).forEach(function (rowEl) {
        // Disabled rows (2★ and 1★) never reflect a pressed state
        if (rowEl.classList.contains('lp-dist-row--disabled')) return;
        var st = parseInt(rowEl.getAttribute('data-stars'), 10);
        var pressed = st === state.filter;
        rowEl.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      });

      // Filter-applied banner — show/hide based on filter state. Strong
      // visible confirmation that the bar click did something.
      if (bannerEl) {
        if (state.filter === 'all') {
          bannerEl.setAttribute('hidden', '');
        } else {
          if (bannerStarsEl) bannerStarsEl.textContent = state.filter;
          bannerEl.removeAttribute('hidden');
        }
      }
    }

    function setFilter(val) {
      if (val === 'all') state.filter = 'all';
      else state.filter = parseInt(val, 10);
      state.page = 1;
      render();
    }

    function goToPage(p) {
      state.page = p;
      render();
      // Scroll the section header into view politely
      var head = $('.lp-reviews-head', root);
      if (head && head.scrollIntoView) {
        head.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    // Event delegation
    root.addEventListener('click', function (e) {
      // Disabled distribution rows (2★, 1★) — no click action
      var disabled = e.target.closest('.lp-dist-row--disabled');
      if (disabled && root.contains(disabled)) return;

      var pill = e.target.closest('[data-filter]');
      if (pill && root.contains(pill)) {
        var val = pill.getAttribute('data-filter');
        setFilter(val);
        return;
      }
      var pageBtn = e.target.closest('[data-page-action]');
      if (pageBtn && root.contains(pageBtn)) {
        var action = pageBtn.getAttribute('data-page-action');
        if (action === 'prev') goToPage(Math.max(1, state.page - 1));
        else if (action === 'next') {
          var total = Math.ceil(filteredReviews().length / currentPageSize());
          goToPage(Math.min(total, state.page + 1));
        }
      }
    });

    // Re-render when viewport crosses the desktop/mobile breakpoint so the
    // grid + page size update. Reset to page 1 to keep state coherent.
    var mq = window.matchMedia(DESKTOP_BP);
    var handleBreakpoint = function () {
      state.page = 1;
      if (state.data) render();
    };
    if (mq.addEventListener) {
      mq.addEventListener('change', handleBreakpoint);
    } else if (mq.addListener) {
      mq.addListener(handleBreakpoint);
    }

    grid.setAttribute('data-loading', 'true');

    fetch(dataUrl, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Reviews data fetch ' + res.status);
        return res.json();
      })
      .then(function (data) {
        state.data = data;
        applyDistributionWidths(root, data.summary.distribution);
        render();
      })
      .catch(function (err) {
        console.error('[lp-reviews]', err);
        grid.innerHTML = '<div class="lp-reviews-empty">Reviews are temporarily unavailable.</div>';
        grid.removeAttribute('data-loading');
      });
  }

  function bootstrap() {
    $$('[data-lp-reviews]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
